import React from 'react'
import { Button, ChevronDownIcon, Text, useModal, Flex, Box, Image } from '@pancakeswap/uikit'
import styled from 'styled-components'
import { DEFAULT_XRP_ICON, useXrpl, XrplCurrency, getTokenLabel } from '../../xrpl'
import XrplCurrencySearchModal from '../SearchModal/XrplCurrencySearchModal'
import { RowBetween } from '../Layout/Row'
import { Input as NumericalInput } from './NumericalInput'

const InputRow = styled.div<{ selected: boolean }>`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: flex-end;
  padding: ${({ selected }) => (selected ? '8px 8px' : '8px 16px')};
`

const CurrencySelectButton = styled(Button).attrs({ variant: 'text', scale: 'sm' })`
  padding: 0 8px;
`

const LabelRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.75rem;
  line-height: 1rem;
  padding: 8px 8px 0 8px;
`

const InputPanel = styled.div`
  display: flex;
  flex-flow: column nowrap;
  position: relative;
  border-radius: 18px;
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid rgba(255, 255, 255, 0.08);
  z-index: 1;
`

const Container = styled.div`
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.input};
  box-shadow: ${({ theme }) => theme.shadows.inset};
`

const TokenIcon = styled(Image)`
  border-radius: 50%;
`

const BalanceText = styled(Text)`
  text-align: right;
  font-size: 12px;
`

const SecondaryValue = styled(Text)`
  text-align: right;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSubtle};
  padding: 0 8px 8px;
`

interface XrplCurrencyInputPanelProps {
  value: string
  onUserInput: (value: string) => void
  onMax?: () => void
  showMaxButton: boolean
  label?: string
  onCurrencySelect: (currency: XrplCurrency) => void
  currency?: XrplCurrency | null
  otherCurrency?: XrplCurrency | null
  disableCurrencySelect?: boolean
  hideBalance?: boolean
  id: string
  balance?: string
  secondaryValue?: string
}

const fallbackIcon = '/images/cake.svg'

const formatBalance = (value?: string) => {
  if (!value) return '0'
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return value
  return numeric.toLocaleString(undefined, { maximumFractionDigits: 6 })
}

export default function XrplCurrencyInputPanel({
  value,
  onUserInput,
  onMax,
  showMaxButton,
  label,
  onCurrencySelect,
  currency,
  otherCurrency,
  disableCurrencySelect = false,
  hideBalance = false,
  id,
  balance,
  secondaryValue,
}: XrplCurrencyInputPanelProps) {
  const { account } = useXrpl()
  const [onPresentCurrencyModal] = useModal(
    <XrplCurrencySearchModal
      onCurrencySelect={onCurrencySelect}
      selectedCurrency={currency}
      otherSelectedCurrency={otherCurrency}
    />,
  )

  return (
    <Box id={id}>
      <Flex mb="8px" alignItems="center" justifyContent="space-between">
        <CurrencySelectButton
          className="open-currency-select-button"
          selected={!!currency}
          onClick={() => {
            if (!disableCurrencySelect) {
              onPresentCurrencyModal()
            }
          }}
        >
          <Flex alignItems="center" justifyContent="space-between">
            {currency ? (
              <TokenIcon
                src={
                  currency.icon ||
                  (currency.isNative || currency.currency === 'XRP' ? DEFAULT_XRP_ICON : fallbackIcon)
                }
                width={28}
                height={28}
                alt={currency.symbol}
              />
            ) : null}
            <Text id="pair" bold ml={currency ? '8px' : 0}>
              {currency ? getTokenLabel(currency) : 'Select a currency'}
            </Text>
            {!disableCurrencySelect && <ChevronDownIcon />}
          </Flex>
        </CurrencySelectButton>
        {account && (
          <BalanceText
            onClick={onMax}
            color="textSubtle"
            style={{ display: 'inline', cursor: 'pointer' }}
            title={balance ?? '0'}
          >
            {!hideBalance && !!currency ? `Balance: ${formatBalance(balance)}` : ' -'}
          </BalanceText>
        )}
      </Flex>
      <InputPanel>
        <Container>
          <LabelRow>
            <RowBetween>
              <NumericalInput
                className="token-amount-input"
                value={value}
                onUserInput={(val) => {
                  onUserInput(val)
                }}
                fontSize="18px"
              />
            </RowBetween>
          </LabelRow>
          <InputRow selected={disableCurrencySelect}>
            {account && currency && showMaxButton && label !== 'To' && (
              <Button onClick={onMax} scale="xs" variant="secondary">
                MAX
              </Button>
            )}
          </InputRow>
        </Container>
        {secondaryValue && <SecondaryValue>{secondaryValue}</SecondaryValue>}
      </InputPanel>
    </Box>
  )
}
